import { Component, h, Host, Prop } from '@stencil/core';
import { AvatarSize } from './types';

export interface AhaAvatar extends HTMLAhaAvatarElement {}

/**
 * Shows a user portrait or their initials. What is displayed will depend on
 * what you pass.
 *
 *   1. Pass an image and it will be shown
 *   2. Pass a label and that will be shown
 *   3. Pass a name and the initials for that name are shown
 *   4. Pass nothing and N/A is displayed
 */
@Component({
  tag: 'aha-avatar',
  styleUrl: 'aha-avatar.css',
  shadow: true,
})
export class AhaAvatar {
  /**
   * URL of the image you wish to display.
   */
  @Prop() src?: string;

  /**
   * The user name. This is used as the alt text for the image and the title.
   */
  @Prop() name?: string;

  /**
   * How big the element is. Images inside will be stretched to a square
   * with this width and height. You must select one of these values, you cannot
   * use any value you want.
   */
  @Prop({ reflect: true }) size?: AvatarSize = 18;

  /**
   * If there is no image then this text label will be shown.
   */
  @Prop() label?: string;

  /**
   * Set if you don't want a border.
   */
  @Prop() borderless?: boolean;

  render() {
    let user;
    let label;

    if (this.label && this.label !== '') {
      label = this.label;
    } else if (this.name && this.name !== '') {
      // Fallback to initials based on the name
      const names = this.name.trim().split(/\s+/);
      // Remove the middle names
      names.splice(1, names.length - 2);
      label = names.map(w => w[0].toUpperCase()).join('');
    }

    if (this.src) {
      user = <img alt={label} src={this.src} />;
    } else if (!label) {
      label = 'N/A';
      user = <fa-icon class="fas fa-user" />;
    } else {
      user = label;
    }

    return <Host title={this.name || this.label}>{user}</Host>;
  }
}
